/**
 * PIN-gated dialogs for every money-moving payout action.
 *
 * All four talk to /admin routes that validate the PIN server-side against
 * ADMIN_PAYOUT_PIN (falling back to ADMIN_REFUND_PIN). The PIN is never stored
 * or remembered between dialogs — it is typed per action on purpose.
 */

import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import API from "Config/API";
import { toast } from "react-toastify";

const T = {
  border: "#e5e7eb",
  ink: "#0f172a",
  muted: "#64748b",
  green: "#059669",
  amber: "#d97706",
  red: "#dc2626",
};

/**
 * The API sends every money field as { paise, amount, display }, already
 * formatted server-side so no browser ever rounds a rupee. Plain numbers are
 * still accepted for the legacy endpoints that return them.
 */
export const money = (value) => {
  if (value && typeof value === "object" && value.display) return value.display;
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/** Integer paise out of a money field, for comparisons and validation. */
export const paiseOf = (value) => {
  if (value && typeof value === "object" && Number.isFinite(value.paise)) {
    return value.paise;
  }
  return Math.round(Number(value || 0) * 100);
};

function PinField({ value, onChange, disabled }) {
  return (
    <TextField
      label="Admin PIN"
      type="password"
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 8))}
      fullWidth
      size="small"
      disabled={disabled}
      autoComplete="off"
      inputProps={{ inputMode: "numeric" }}
      helperText="Required for every payout action."
    />
  );
}

function useAction(onDone, onClose) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const run = async (url, payload, successMessage) => {
    setError("");
    setBusy(true);
    try {
      const { data } = await API.post(url, payload);
      if (data?.success === false) {
        setError(data?.message || "Action failed.");
        return;
      }
      toast.success(data?.message || successMessage);
      onDone?.();
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Something went wrong. Try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return { busy, error, setError, run };
}

// ── Approve ─────────────────────────────────────────────────────────────────
export function ApproveDialog({ open, onClose, request, gatewayReady, onDone }) {
  const [pin, setPin] = useState("");
  const [mode, setMode] = useState("razorpay");
  const [note, setNote] = useState("");
  const { busy, error, setError, run } = useAction(onDone, onClose);

  useEffect(() => {
    if (!open) return;
    setPin("");
    setNote("");
    setMode(gatewayReady ? "razorpay" : "manual");
    setError("");
  }, [open, gatewayReady, setError]);

  if (!request) return null;

  const submit = () => {
    if (!/^\d{4,8}$/.test(pin)) return setError("Enter the 4–8 digit PIN.");
    run(
      `/admin/payouts/${request.requestId}/approve`,
      { pin, mode, note: note.trim() || undefined },
      "Payout approved."
    );
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: T.ink }}>
        Approve payout
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Box
            sx={{
              border: `1px solid ${T.border}`,
              borderRadius: 2,
              p: 1.5,
              bgcolor: "#f8fafc",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 800, color: T.success }}>
              {money(request.amount)}
            </Typography>
            <Typography variant="body2" sx={{ color: T.muted, mt: 0.5 }}>
              {request.pharmacyName || request.pharmacyId}
            </Typography>
            <Typography variant="caption" sx={{ color: T.muted }}>
              {request.bankDetails?.bankName || "Bank"} ·{" "}
              {request.bankDetails?.accountNumber || "no account"} ·{" "}
              {request.bankDetails?.ifscCode || "no IFSC"}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" sx={{ color: T.muted, fontWeight: 600 }}>
              How is the money being sent?
            </Typography>
            <ToggleButtonGroup
              value={mode}
              exclusive
              size="small"
              fullWidth
              sx={{ mt: 0.5 }}
              onChange={(_, v) => v && setMode(v)}
            >
              <ToggleButton value="razorpay" disabled={!gatewayReady}>
                Razorpay (auto)
              </ToggleButton>
              <ToggleButton value="manual">Manual transfer</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* When the account has not been checked, the person approving IS
              the check. Saying so at the moment of approval is the only place
              it can actually change what someone does. */}
          {request.bankVerified === false && (
            <Alert severity="warning" sx={{ fontSize: 13 }}>
              <b>This bank account has not been verified.</b> Check the account
              number and IFSC against a cancelled cheque or passbook before you
              approve — a payout into a valid wrong account cannot be recovered.
            </Alert>
          )}
          {request.bankVerified === true && (
            <Alert severity="success" sx={{ fontSize: 13 }}>
              Bank account was verified when this was requested.
            </Alert>
          )}

          {!gatewayReady && (
            <Alert severity="info" sx={{ fontSize: 13 }}>
              RazorpayX isn't configured on the server, so this payout has to be
              transferred from the bank and then marked paid with its UTR.
            </Alert>
          )}
          {mode === "razorpay" && gatewayReady && (
            <Alert severity="warning" sx={{ fontSize: 13 }}>
              Approving sends {money(request.amount)} to the pharmacy's bank
              account immediately. This cannot be undone.
            </Alert>
          )}

          <TextField
            label="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />

          <PinField value={pin} onChange={setPin} disabled={busy} />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ color: T.muted }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy}
          sx={{ bgcolor: T.success, "&:hover": { bgcolor: "#047857" } }}
        >
          {busy ? <CircularProgress size={20} color="inherit" /> : "Approve & send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Reject ──────────────────────────────────────────────────────────────────
const REJECT_REASONS = [
  "Bank details incorrect or unverified",
  "KYC / documents pending",
  "Amount under dispute",
  "Duplicate request",
  "Other",
];

export function RejectDialog({ open, onClose, request, onDone }) {
  const [pin, setPin] = useState("");
  const [preset, setPreset] = useState(REJECT_REASONS[0]);
  const [other, setOther] = useState("");
  const { busy, error, setError, run } = useAction(onDone, onClose);

  useEffect(() => {
    if (!open) return;
    setPin("");
    setPreset(REJECT_REASONS[0]);
    setOther("");
    setError("");
  }, [open, setError]);

  if (!request) return null;

  const reason = preset === "Other" ? other.trim() : preset;

  const submit = () => {
    if (!/^\d{4,8}$/.test(pin)) return setError("Enter the 4–8 digit PIN.");
    if (reason.length < 3) return setError("Give a reason for the rejection.");
    run(
      `/admin/payouts/${request.requestId}/reject`,
      { pin, reason },
      "Request rejected."
    );
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: T.ink }}>
        Reject payout
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" sx={{ color: T.muted }}>
            {money(request.amount)} goes straight back into{" "}
            {request.pharmacyName || request.pharmacyId}'s withdrawable balance.
          </Typography>

          <TextField
            select
            label="Reason"
            value={preset}
            onChange={(e) => setPreset(e.target.value)}
            size="small"
            fullWidth
          >
            {REJECT_REASONS.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </TextField>

          {preset === "Other" && (
            <TextField
              label="Write the reason"
              value={other}
              onChange={(e) => setOther(e.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={2}
            />
          )}

          <PinField value={pin} onChange={setPin} disabled={busy} />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ color: T.muted }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy}
          sx={{ bgcolor: T.error, "&:hover": { bgcolor: "#b91c1c" } }}
        >
          {busy ? <CircularProgress size={20} color="inherit" /> : "Reject"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Mark paid (money moved outside Razorpay) ────────────────────────────────
export function MarkPaidDialog({ open, onClose, request, onDone }) {
  const [pin, setPin] = useState("");
  const [utr, setUtr] = useState("");
  const { busy, error, setError, run } = useAction(onDone, onClose);

  useEffect(() => {
    if (!open) return;
    setPin("");
    setUtr("");
    setError("");
  }, [open, setError]);

  if (!request) return null;

  const submit = () => {
    if (!/^\d{4,8}$/.test(pin)) return setError("Enter the 4–8 digit PIN.");
    if (!utr.trim()) return setError("Enter the UTR / bank reference.");
    run(
      `/admin/payouts/${request.requestId}/mark-paid`,
      { pin, utr: utr.trim() },
      "Marked as paid."
    );
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: T.ink }}>
        Mark as paid
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Alert severity="warning" sx={{ fontSize: 13 }}>
            Only do this once {money(request.amount)} has actually left the bank
            account. It closes the request for good.
          </Alert>
          <TextField
            label="UTR / bank reference"
            value={utr}
            onChange={(e) => setUtr(e.target.value)}
            size="small"
            fullWidth
          />
          <PinField value={pin} onChange={setPin} disabled={busy} />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ color: T.muted }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy}
          sx={{ bgcolor: T.success, "&:hover": { bgcolor: "#047857" } }}
        >
          {busy ? <CircularProgress size={20} color="inherit" /> : "Mark paid"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Manual wallet adjustment ────────────────────────────────────────────────
export function AdjustWalletDialog({ open, onClose, wallet, onDone }) {
  const [pin, setPin] = useState("");
  const [type, setType] = useState("credit");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const { busy, error, setError, run } = useAction(onDone, onClose);

  useEffect(() => {
    if (!open) return;
    setPin("");
    setType("credit");
    setAmount("");
    setReason("");
    setError("");
  }, [open, setError]);

  if (!wallet) return null;

  const submit = () => {
    if (!/^\d{4,8}$/.test(pin)) return setError("Enter the 4–8 digit PIN.");
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0)
      return setError("Enter a valid amount.");
    // Compared in paise so a ₹0.01 float difference can't block a valid debit.
    if (type === "debit" && Math.round(value * 100) > paiseOf(wallet.balance))
      return setError("Amount is more than the pharmacy's balance.");
    if (reason.trim().length < 3) return setError("Give a reason.");

    run(
      `/admin/pharmacy-wallets/${wallet.pharmacyId}/adjust`,
      { pin, amount: value, type, reason: reason.trim() },
      "Wallet updated."
    );
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: T.ink }}>
        Adjust wallet
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" sx={{ color: T.muted }}>
            {wallet.pharmacyName || wallet.pharmacyId} · balance{" "}
            <b>{money(wallet.balance)}</b>
          </Typography>

          <ToggleButtonGroup
            value={type}
            exclusive
            size="small"
            fullWidth
            onChange={(_, v) => v && setType(v)}
          >
            <ToggleButton value="credit">Credit</ToggleButton>
            <ToggleButton value="debit">Debit</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            label="Amount (₹)"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
            size="small"
            fullWidth
            inputProps={{ inputMode: "decimal" }}
          />
          <TextField
            label="Reason (goes into the ledger)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
          <PinField value={pin} onChange={setPin} disabled={busy} />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ color: T.muted }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy}
          sx={{ bgcolor: type === "credit" ? T.success : T.warning }}
        >
          {busy ? (
            <CircularProgress size={20} color="inherit" />
          ) : type === "credit" ? (
            "Credit wallet"
          ) : (
            "Debit wallet"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
